import { FocusEvent, useId, useRef, useState } from 'react';
import {
	SelectProp,
	SortOrder,
	SelectOption,
	SortOrderOption,
} from './SelectProps.ts';
import { getKeys } from '../../../../_helpers/keyboard.ts';
import { Option } from './Option.tsx';
import { joinString } from '../../../../_helpers/join-string.ts';
import { sendDataLinkEvent } from '../_helpers/dataLinkEventHelper.ts';
import { useComponentContext } from '../_helpers/componentContext.tsx';

import './Select.scss';

const Select: React.FC<SelectProp> = (props) => {
	const componentName = useComponentContext().componentName;
	const id = useId();
	const selectRef = useRef<HTMLDivElement>(null);
	const { label, options, orderLabels, onSelect, defaultOption } = props;
	const [opened, setOpened] = useState(false);
	const [selectedOption, setSelectedOption] = useState(
		options.find((o) => o.value === defaultOption) ?? options[0]
	);

	const [focusedOption, setFocusedOption] = useState<SelectOption>();
	const [sortOrderOptions] = useState<SortOrderOption[]>(
		orderLabels
			? [
					{
						label: orderLabels.ascending,
						value: SortOrder.ascending,
					},
					{
						label: orderLabels.descending,
						value: SortOrder.descending,
					},
				]
			: []
	);
	const [sortOrder, setSortOrder] = useState<SortOrderOption>(
		getOrderOption(
			props.defaultOrder ?? SortOrder.ascending,
			sortOrderOptions
		) || sortOrderOptions?.[0]
	);
	const dropdownButton = useRef<HTMLDivElement>(null);

	const isOpen = opened ? ' is-opened' : '';

	const onSelectHandler = (option: SelectOption, sort: SortOrder) => {
		onSelect(option.value, sort);
		setOpened(true);
	};

	const handleBlur = (e: FocusEvent) => {
		if (selectRef?.current?.contains(e.relatedTarget)) {
			return;
		}
		setOpened(false);
	};
	return (
		<div
			tabIndex={-1}
			ref={selectRef}
			className={'blk-ac-select' + isOpen}
			onClick={() => {
				setOpened(!opened);
				if (opened) {
					(document.activeElement as HTMLElement).blur();
				} else {
					dropdownButton?.current?.focus();
				}
			}}
		>
			<span className="select-label blk-ac-body-m">{label}:</span>
			<div
				tabIndex={0}
				ref={dropdownButton}
				className="select-option selected select-selection"
				onKeyDown={(e) => {
					getKeys(e).isSpace && e.preventDefault();
					const keys = getKeys(e);
					if (keys.isEnter || keys.isSpace) {
						setOpened(!opened);
						setFocusedOption(opened ? undefined : options[0]);
					}
				}}
				onKeyUp={(e) => {
					e.stopPropagation();
					e.preventDefault();
				}}
				onBlur={handleBlur}
				id={id}
				role="button"
				aria-controls={id + '-select'}
				aria-expanded={opened}
				aria-live="polite"
			>
				<div
					className="labels"
					aria-label={joinString([
						label,
						selectedOption.label,
						sortOrder?.label,
					])}
				>
					<span className="select-option-label blk-ac-body-m-bold" aria-hidden>
						{selectedOption.label}
					</span>
					{/* This makes sure that there is enough space to fit even the largest label */}
					{options.map((option) => (
						<span
							key={option.value}
							aria-hidden
							className="select-option-label blk-ac-body-m-bold hidden-label"
						>
							{option.label}
						</span>
					))}
				</div>
				<i className="icon fas fa-chevron-up" aria-hidden></i>
			</div>
			<div
				title="Select"
				className={'select-dropdown blk-ac-no-select' + isOpen}
				id={id + '-select'}
				onBlur={handleBlur}
				onKeyDown={(e) => {
					const keys = getKeys(e);
					const allOptions = [
						...options,
						...(sortOrderOptions ? sortOrderOptions : []),
					];
					const index = allOptions.findIndex(
						(o) => o.value === focusedOption?.value
					);

					if ((keys.isUp || keys.isShiftTab) && index > 0) {
						setFocusedOption(allOptions[index - 1]);
						e.preventDefault();
					}
					if ((keys.isDown || keys.isTab) && index < allOptions.length - 1) {
						setFocusedOption(allOptions[index + 1]);
						e.preventDefault();
					}
					if (keys.isSpace || keys.isEnter) {
						e.preventDefault();
					}
					if (
						keys.isEscape ||
						(keys.isShiftTab && index === 0) ||
						(keys.isTab && index === allOptions.length - 1)
					) {
						e.preventDefault();
						setFocusedOption(undefined);
						setOpened(false);
						dropdownButton?.current?.focus();
					}
				}}
			>
				<div className="select-group">
					<ul className="select-list" role="listbox">
						{options.map((option) => (
							<Option
								key={option.value}
								option={option}
								opened={opened}
								autofocus={focusedOption === option}
								isSelected={option.value === selectedOption.value}
								onSelect={() => {
									setSelectedOption(option);
									onSelectHandler(option, SortOrder[sortOrder.value]);
									sendDataLinkEvent(componentName + '|sortBy|' + option.label);
								}}
							/>
						))}
					</ul>
				</div>
				{sortOrderOptions && (
					<>
						<div className="divider" aria-hidden="true"></div>
						<div className="select-group">
							<ul className="select-list" role="listbox">
								{sortOrderOptions.map((option) => (
									<Option
										key={option.value}
										option={option}
										opened={opened}
										autofocus={focusedOption === option}
										isSelected={option.value === sortOrder.value}
										onSelect={() => {
											setSortOrder(option);
											onSelectHandler(selectedOption, SortOrder[option.value]);
										}}
									/>
								))}
							</ul>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default Select;

const getOrderOption = (
	order: SortOrder,
	sortOrderOptions: SortOrderOption[]
): SortOrderOption | undefined => {
	if (!order || !sortOrderOptions) return undefined;
	return sortOrderOptions.find((orderOption) => orderOption.value === order);
};
